import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useConsoleRole } from '../../session/SessionContext';
import { TUTORIALS, type GuideData, type GuideRole } from '../../content/guideData';
import { sx } from '../../ui/designStyles';
import { guideHows, guideScreens } from './guideVals';
import { useGuideProgress } from './useGuideProgress';

/**
 * The large-text guide for Resident, Family and Aithora Home (design isBig): one step at a
 * time, 60px targets, then the end card with How do I. Next ticks the step on the server
 * (useGuideProgress); a read-only view still pages through, it only never saves.
 */
const S = {
  wrap: 'min-height:100vh;box-sizing:border-box;padding:24px 20px 40px;display:flex;justify-content:center;',
  col: 'width:100%;max-width:720px;display:flex;flex-direction:column;gap:22px;',
  top: 'display:flex;align-items:center;gap:10px 14px;flex-wrap:wrap;',
  logo: 'font-size:24px;font-weight:800;letter-spacing:-.02em;',
  kick: 'font-size:19px;letter-spacing:.08em;text-transform:uppercase;color:#2F5C97;font-weight:700;',
  back: 'display:inline-flex;align-items:center;min-height:60px;padding:0 20px;border-radius:12px;font-size:19px;font-weight:700;text-decoration:none;color:#22272E;border:2px solid rgba(34,39,46,.25);',
  stepRow: 'display:flex;align-items:center;gap:14px;flex-wrap:wrap;',
  stepLabel: 'font-size:22px;font-weight:800;font-variant-numeric:tabular-nums;',
  toHow: 'display:inline-flex;align-items:center;min-height:60px;padding:0 6px;font-size:19px;font-weight:700;text-decoration:underline;text-underline-offset:4px;color:#2F5C97;',
  title: 'font-size:36px;font-weight:800;letter-spacing:-.025em;line-height:1.12;text-wrap:balance;',
  lead: 'font-size:23px;font-weight:600;color:#2F5C97;line-height:1.4;margin-top:14px;text-wrap:pretty;',
  body: 'font-size:21px;color:#22272E;line-height:1.55;margin-top:14px;text-wrap:pretty;',
  talk: 'display:flex;align-items:center;gap:12px;padding:16px 18px;border-radius:14px;background:rgba(79,138,214,.1);',
  bBack: 'flex:1 1 160px;display:inline-flex;align-items:center;justify-content:center;min-height:68px;border-radius:14px;font-size:21px;font-weight:700;text-decoration:none;color:#22272E;border:2px solid rgba(34,39,46,.28);',
  bNext: 'flex:2 1 220px;display:inline-flex;align-items:center;justify-content:center;min-height:68px;border-radius:14px;font-size:22px;font-weight:800;text-decoration:none;color:#fff;background:#2F5C97;',
  how: 'display:flex;align-items:center;gap:16px;min-height:76px;padding:12px 20px;box-sizing:border-box;border-radius:14px;border:2px solid rgba(47,92,151,.45);text-decoration:none;color:#22272E;',
  again: 'display:inline-flex;align-items:center;min-height:60px;margin-top:14px;font-size:19px;font-weight:700;color:#2F5C97;text-decoration:underline;text-underline-offset:4px;',
  foot: 'font-size:19px;color:#3C454F;line-height:1.5;',
  err: 'font-size:17px;color:#B3261E;line-height:1.45;',
};

export function GuideBig({ name, R }: { G: GuideData; raw: string; name: string; R: GuideRole }) {
  const { role } = useConsoleRole();
  const prog = useGuideProgress(name);
  const done = prog.done;
  const { screens, end } = useMemo(() => guideScreens(R, TUTORIALS, role), [R, role]);
  const hows = useMemo(() => guideHows(R, role), [R, role]);
  const [bigI, setBigI] = useState<number | null>(null);
  const [anim, setAnim] = useState(0);
  const tot = screens.length;
  const doneN = screens.filter((s) => done[s.title]).length;
  const nextI = screens.findIndex((s) => !done[s.title]);
  const bi = Math.max(0, Math.min(tot, bigI == null ? (nextI < 0 ? tot : nextI) : bigI));
  const s = screens[bi] || null;
  const onStep = !!s;
  const go = (i: number) => {
    setBigI(i);
    setAnim((a) => a + 1);
  };
  const animCss = `animation:${anim % 2 ? 'gRiseA' : 'gRiseB'} .34s cubic-bezier(.22,.61,.36,1) both;`;
  const next = async () => {
    if (!s) return;
    // Paging never waits on the tick: a person reading along keeps their place.
    if (!prog.readOnly && !done[s.title]) void prog.setStep(s.title, true);
    go(bi + 1);
  };
  const restart = async () => {
    if (!prog.readOnly) await prog.reset();
    go(0);
  };
  const endLines = end ? [...(end.lines || []), ...(end.ask ? [end.ask] : [])] : [];

  return (
    <div data-screen-label="Guide · plain" style={sx(S.wrap)}>
      <div style={sx(S.col)}>
        <div style={sx(S.top)}>
          <span style={sx(S.logo)}>aithora</span>
          <span style={sx(S.kick)}>Guide</span>
          <span style={sx('flex:1 1 20px;')} />
          <Link to="/" className="g-back" style={sx(S.back)}>
            {'← Back'}
          </Link>
        </div>

        <div style={sx(S.stepRow)}>
          <span style={sx(S.stepLabel)}>{onStep ? `Step ${bi + 1} of ${tot}` : doneN === tot ? 'All done' : `${doneN} of ${tot} done`}</span>
          <span style={sx('display:flex;gap:9px;align-items:center;')} aria-hidden="true">
            {screens.map((x, i) => (
              <span
                key={x.title}
                style={sx(
                  'display:block;width:14px;height:14px;border-radius:50%;transition:background .3s;' +
                    (i === bi ? 'background:#2F5C97;box-shadow:0 0 0 4px rgba(79,138,214,.22);' : done[x.title] ? 'background:#1F6B52;' : 'background:rgba(34,39,46,.2);'),
                )}
              />
            ))}
          </span>
          <span style={sx('flex:1 1 10px;')} />
          {onStep && (
            <button type="button" className="dlink" style={sx(S.toHow)} onClick={() => go(tot)}>
              {'How do I…'}
            </button>
          )}
        </div>

        {onStep && s && (
          <>
            <div key={anim} style={sx(animCss)}>
              <div style={sx(S.title)}>{s.title}</div>
              {!!s.lead && <div style={sx(S.lead)}>{s.lead}</div>}
              {!!s.body && <div style={sx(S.body)}>{s.body}</div>}
              {s.points.length > 0 && (
                <div style={sx('display:flex;flex-direction:column;gap:12px;margin-top:18px;')}>
                  {s.points.map((x, n) => (
                    <div key={n} style={sx('display:flex;gap:14px;font-size:21px;line-height:1.45;')}>
                      <span style={sx('width:10px;height:10px;border-radius:50%;background:#2F5C97;flex:0 0 10px;margin-top:.5em;')} />
                      <span>{x}</span>
                    </div>
                  ))}
                </div>
              )}
              {s.pairs.length > 0 && (
                <div style={sx('display:flex;flex-direction:column;margin-top:16px;')}>
                  {s.pairs.map((p, n) => (
                    <div key={n} style={sx('padding:14px 0;border-top:1.5px solid rgba(34,39,46,.14);')}>
                      <div style={sx('font-size:21px;font-weight:700;')}>{p.k}</div>
                      <div style={sx('font-size:20px;color:#3C454F;line-height:1.5;margin-top:4px;')}>{p.v}</div>
                    </div>
                  ))}
                </div>
              )}
              {!!s.note && <div style={sx('font-size:19px;color:#3C454F;line-height:1.55;margin-top:18px;text-wrap:pretty;')}>{s.note}</div>}
            </div>
            <div style={sx(S.talk)}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2F5C97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={sx('flex:0 0 26px;')} aria-hidden="true">
                <rect x="9" y="3" width="6" height="11" rx="3" />
                <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
              </svg>
              <span style={sx('font-size:19px;line-height:1.45;color:#22272E;')}>{'Rather talk? Say “Hey Siri, …” on your phone or tablet.'}</span>
            </div>
            <div style={sx('display:flex;gap:12px;flex-wrap:wrap;')}>
              {bi > 0 && (
                <button type="button" className="dlink g-bback" style={sx(S.bBack)} onClick={() => go(bi - 1)}>
                  {'← Back'}
                </button>
              )}
              <button type="button" className="dlink g-bnext" style={sx(S.bNext)} onClick={next}>
                {bi === tot - 1 ? 'Finish ✓' : 'Next →'}
              </button>
            </div>
          </>
        )}

        {!onStep && (
          <div key={anim} style={sx(animCss)}>
            <div style={sx('display:flex;align-items:center;gap:14px;')}>
              <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:56px;height:56px;border-radius:50%;background:#1F6B52;color:#fff;font-size:28px;font-weight:800;animation:gPop .45s ease both;')}>
                {'✓'}
              </span>
              <span style={sx('font-size:34px;font-weight:800;letter-spacing:-.025em;line-height:1.12;')}>{end ? end.title : 'You’re all set'}</span>
            </div>
            {endLines.map((x, n) => (
              <div key={n} style={sx('font-size:21px;line-height:1.55;margin-top:12px;text-wrap:pretty;')}>
                {x}
              </div>
            ))}
            <div style={sx('font-size:26px;font-weight:800;letter-spacing:-.02em;margin-top:28px;')}>{'How do I…'}</div>
            <div style={sx('display:flex;flex-direction:column;gap:12px;margin-top:12px;')}>
              {hows.map((h) => (
                <Link key={h.i} to={h.href || '/'} className="g-bhow" style={sx(S.how)}>
                  <span style={sx('flex:1 1 auto;min-width:0;')}>
                    <span style={sx('display:block;font-size:22px;font-weight:800;')}>{h.q}</span>
                    <span style={sx('display:block;font-size:19px;color:#3C454F;line-height:1.4;margin-top:3px;')}>{h.a}</span>
                  </span>
                  <span style={sx('flex:0 0 auto;font-size:26px;font-weight:800;color:#2F5C97;')}>{'→'}</span>
                </Link>
              ))}
            </div>
            <button type="button" className="dlink" style={sx(S.again)} onClick={restart}>
              Go through it again
            </button>
          </div>
        )}

        {prog.saveError && <div style={sx(S.err)} role="alert">{`Not saved: ${prog.saveError}`}</div>}
        <div style={sx(S.foot)}>
          Still stuck? Ask someone on your care team, or email{' '}
          <a className="g-mail" href="mailto:info@aithoracare.com">
            info@aithoracare.com
          </a>
          .
        </div>
      </div>
    </div>
  );
}
